import { NetworkStatus, QueryHookOptions, useQuery } from '@apollo/client';
import {
  ChatMessageGroup,
  formatMessageData,
  groupMessagesByParticipantIdentity,
} from '@provision/chat-utils';
import { ConversationParticipationMode } from '@provision/conversation-types';
import { getFullName } from '@provision/formatting-utils';
import { useCallback, useMemo, useRef } from 'react';
import { config } from 'src/core/config';
import {
  ConversationParticipant,
  UseConversationParams,
} from 'src/graphql/entities/conversation';
import { mapMessagesForChatUI } from 'src/graphql/entities/conversation/conversation.mappings';
import { DEFAULT_MESSAGE_LIMIT } from 'src/graphql/entities/conversation-message/constants';
import { GET_CONVERSATION_MESSAGES_QUERY } from 'src/graphql/entities/conversation-message/conversation-message.operations';
import {
  ConversationParticipantItem,
  useGroupedConversationParticipants,
} from 'src/graphql/entities/conversation-participants';
import { useConversationParticipationRecords } from 'src/graphql/entities/conversation-participation/hooks/use-conversation-participation-records';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { useParticipationBadgeMap } from 'src/pages/Conversations/Chat/utils';
import { ConversationMessageRaw } from 'src/types';
import { isSameRecords } from 'src/utils/objects';

export type GetConversationMessageQuery = {
  getConversationMessage: Records<ConversationMessageRaw>;
};

type UseConversationMessageParams = UseConversationParams & {
  user: ReturnType<typeof useCurrentUserProfile>['user'];
  options?: QueryHookOptions<GetConversationMessageQuery>;
};

export const useConversationMessages = (params: UseConversationMessageParams) => {
  const { conversationId, limit, options, user } = params;

  const messagesCacheRef = useRef<Record<string, ChatMessageGroup<ConversationParticipant>[]>>(
    {},
  );

  const conversationQueryFilters = useMemo(() => {
    const filters = [];

    if (conversationId) {
      filters.push({
        attribute: 'conversationId',
        operator: '=',
        value: conversationId,
      });
    }

    return filters;
  }, [conversationId]);

  const skip = !conversationQueryFilters.length || !conversationId || options?.skip;

  const { conversationParticipations } = useConversationParticipationRecords({
    conversationId: conversationId ?? '',
    skip,
    includeDeleted: true,
  });

  const {
    data,
    client,
    fetchMore,
    loading: messagesLoading,
    variables,
    networkStatus,
    ...rest
  } = useQuery<GetConversationMessageQuery, QueryVariables>(GET_CONVERSATION_MESSAGES_QUERY, {
    variables: getConversationMessagesQueryVariables({
      filters: conversationQueryFilters,
      limit,
    }),
    notifyOnNetworkStatusChange: true,
    ...options,
    skip,
  });

  const currentOffset = data?.getConversationMessage.metadata?.offset ?? 0;

  // The cacheRef helps to avoid UI blinking when conversation participants change
  // as any participation change updates query parameters, and the query starts
  // to returns nothing (as nothing is cached for such a filter combination).
  type ParticipantsCache = {
    groupedParticipants: Record<ConversationParticipationMode, ConversationParticipantItem[]>;
    participants: ConversationParticipantItem[];
  };

  const cacheRef = useRef<ParticipantsCache | undefined>(undefined);

  const { groupedParticipants: rawGroup, participants: rawList } =
    useGroupedConversationParticipants(conversationParticipations);

  if (rawList?.length) {
    cacheRef.current = { groupedParticipants: rawGroup, participants: rawList };
  }

  const groupedParticipants = useMemo(
    () =>
      (rawList?.length ? rawGroup : cacheRef.current?.groupedParticipants) ?? {
        [ConversationParticipationMode.Assist]: [],
        [ConversationParticipationMode.Engage]: [],
      },
    [rawList, rawGroup],
  );

  const participants = (rawList?.length ? rawList : cacheRef.current?.participants) ?? [];

  const badgeMap = useParticipationBadgeMap();

  const messages = useMemo(() => {
    if (!conversationId) {
      return [];
    }

    const messages = (data?.getConversationMessage.records ?? []).map((message) => ({
      ...message,
      messageDispatches: message.messageDispatches
        .map((dispatch) => ({
          ...dispatch,
          participation: conversationParticipations.find(
            (participation) => participation.id === dispatch.conversationParticipationId,
          )!,
        }))
        .filter((i) => i.participation !== undefined),
    }));

    if (!messages && messagesCacheRef.current[conversationId]?.length) {
      return messagesCacheRef.current[conversationId];
    }

    const participantsMap = Object.values(groupedParticipants)
      .flat()
      .reduce(
        (acc, p) => {
          acc[p.conversationParticipantId] = p.client || p.platformUser || p.rsmEmail;
          return acc;
        },
        // This is needed to immediately show current user
        // info in sent messages for conversations where they weren't
        // a participant yet.
        user
          ? {
              [user.userId]: {
                ...user,
                isActive: true,
                phoneNumber: '',
                avatarFileKey: user.avatarFileKey,
                fullName: getFullName(user),
              } as ConversationParticipant,
            }
          : ({} as Record<string, ConversationParticipant>),
      );

    const newMessages = groupMessagesByParticipantIdentity<
      ReturnType<typeof formatMessageData>,
      ConversationParticipant
    >({
      conversationId,
      messages,
      baseUrl: config.s3AttachmentsUrl,
      // @ts-expect-error figure out what's wrong with participant typings
      formatMessage: formatMessageData,
      participants: participantsMap,
    });

    const inactiveUserIds = groupedParticipants[ConversationParticipationMode.Assist]
      .filter((p) => p.platformUser && !p.platformUser.isActive)
      .map((p) => p.participantIdentity);

    const mappedMessages = mapMessagesForChatUI({
      conversationId,
      items: newMessages,
      conversationParticipants: {
        engage: groupedParticipants[ConversationParticipationMode.Engage],
        assist: groupedParticipants[ConversationParticipationMode.Assist],
      },
      badgeMap,
      inactiveUserIds,
    });

    messagesCacheRef.current[conversationId] = mappedMessages;

    return mappedMessages;
  }, [
    user,
    conversationParticipations,
    conversationId,
    groupedParticipants,
    data?.getConversationMessage.records,
    badgeMap,
  ]);

  const { loadMore: loadMoreMessages, hasReachedEnd } =
    useLoadMore<GetConversationMessageQuery>({
      offset: data?.getConversationMessage?.records?.length ?? 0,
      fetchMore,
      params: variables?.params!,
      deduplicationPredicate: isSameRecords,
    });

  // Prevent loading more messages if previous request hasn't been completed yet.
  const loadMore = useCallback<typeof loadMoreMessages>(
    (...args) => {
      if (messagesLoading) {
        return Promise.resolve();
      }
      return loadMoreMessages(...args);
    },
    [loadMoreMessages, messagesLoading],
  );

  const totalRecords = data?.getConversationMessage.metadata?.totalRecords ?? 0;

  return {
    participants,
    messages,
    client,
    hasMore: data?.getConversationMessage.metadata?.totalRecords
      ? totalRecords > (data?.getConversationMessage?.records?.length ?? 0)
      : !hasReachedEnd,
    offset: currentOffset,
    loadMore,
    // if there are already some messages we don't show loading state
    // as it will produce unwanted re-renders
    loading: !messages?.length && messagesLoading,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    ...rest,
  };
};

export function getConversationMessagesQueryVariables({
  filters,
  limit,
  offset,
}: {
  filters: ConditionInput[];
  limit?: number;
  offset?: number;
}): QueryVariables {
  return {
    params: {
      filters,
      offset: offset ?? 0,
      limit: limit ?? DEFAULT_MESSAGE_LIMIT,
      sorting: { attribute: 'createdAt', desc: true },
      includeDeleted: true,
    },
    skipTotalRecords: true,
  };
}
